import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { useLazyQuery, useMutation } from '@apollo/client/react'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal } from 'components/UI'
// hooks
import { useCurrentUser, useInitialValues, useModalData } from 'hooks/index'
// constants
import { ENTITIES, Modals } from 'constants/index'
// types
import { IRiskTemplate, IRiskView, IRiskViewResponse } from 'types/graphql'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
import { addRiskView, updateRiskView as updateRiskViewAction } from 'store/reducers/riskViews'
// icons
import { CloseIcon } from 'components/UI/Icons'
// view components
import AddRiskViewForm, { RiskViewFormData } from 'components/Modals/RiskViewModal/components/Form'
// graphql
import { RISK_TEMPLATE_QUERY } from 'graphql/risk/queries'
import { USERS_OR_POSITIONS_QUERY } from 'graphql/user/queries'
import { RISK_VIEW_CREATE_MUTATION, RISK_VIEW_UPDATE_MUTATION } from 'graphql/risk/mutations'
// utils
import { v4 as uuid } from 'uuid'
import { handleError } from 'utils/errors/handlers'
import { get, omit, uniq } from 'lodash'
import { getMemberOrPositionLabel } from 'utils/systemProcess'

const getRiskViewInput = (data: RiskViewFormData) => {
  const { name, riskTemplate, fields, relatedForms } = data

  const relatedFormsIds = relatedForms?.map((item) => item.value.id) ?? []

  // Drop fields of related forms that are no longer selected (or whose template is gone)
  const normalizedFields = Object.values<any>(fields || {})
    .filter((field) => field.active && (!field.riskTemplateId || relatedFormsIds.includes(field.riskTemplateId)))
    .map((field) => omit(field, 'active'))

  const customColumns = data.customColumns?.map((column) => ({
    id: column.id || uuid(),
    name: column.name,
    sources: column.sources?.map((source) => ({
      fieldId: source.fieldId.value,
      templateId: source.templateId.value
    }))
  }))

  const availableToUserOrPositionIds = (data.availableToUserOrPositionIds || [])
    .filter((item) => item?.value)
    .map((item) => item.value)

  return {
    name,
    riskTemplateId: riskTemplate.value.id,
    settings: {
      fields: normalizedFields
    },
    customColumns,
    availableToUserOrPositionIds
  }
}

type QueryResponse = {
  riskViewCreate: IRiskViewResponse
}

const AddRiskViewDrawer = () => {
  const dispatch = useDispatch()

  const user = useCurrentUser()

  const [getTemplate] = useLazyQuery(RISK_TEMPLATE_QUERY)
  const [getUsersOrPositions] = useLazyQuery(USERS_OR_POSITIONS_QUERY)

  const [riskViewCreate] = useMutation<QueryResponse>(RISK_VIEW_CREATE_MUTATION)
  const [riskViewUpdate] = useMutation(RISK_VIEW_UPDATE_MUTATION)

  const { selectRiskView } = React.useContext(RiskRegisterContext)

  const {
    hideModal,
    modalData: { isOpen, riskView }
  } = useModalData<{ riskView: IRiskView }>({ modalType: Modals.RISK_VIEW_CREATE_MODAL })

  const riskTemplates = useSelector(riskTemplatesSelector)

  const getAvailableToOptions = async (ids: string[]) => {
    if (!ids.length) {
      return []
    }

    const response = await getUsersOrPositions({
      variables: { filter: { companyId: user.companyId, ids } }
    })

    return get(response, 'data.usersOrPositions.data', []).map((item) => ({
      label: getMemberOrPositionLabel(item),
      value: item.id,
      entity: item.description ? ENTITIES.POSITION : ENTITIES.USER
    }))
  }

  const getInitialValue = async () => {
    try {
      if (!riskView) {
        return {}
      }

      const response = await getTemplate({
        variables: {
          filter: {
            ids: [riskView.riskTemplate.id]
          }
        }
      })

      const riskTemplate: IRiskTemplate = get(response, 'data.riskTemplates.data[0]')

      const relatedFormsIds = uniq(
        riskView.settings.fields.filter((item) => item.riskTemplateId).map((item) => item.riskTemplateId)
      )

      const availableToUserOrPositionIds = await getAvailableToOptions(riskView.availableToUserOrPositionIds || [])

      return {
        availableToUserOrPositionIds,
        name: riskView.name,
        riskTemplate: { value: riskTemplate, label: riskTemplate.name },
        fields: riskView.settings.fields.reduce(
          (acc, cur) => ({
            ...acc,
            [cur.fieldLinkId || cur.fieldId]: {
              ...omit(cur, '__typename'),
              active: cur.active ?? true
            }
          }),
          {}
        ),
        relatedForms: riskTemplates
          .filter((item) => relatedFormsIds.includes(item.id))
          .map((item) => ({ label: item.name, value: item })),
        customColumns: riskView.customColumns?.map((column) => ({
          ...column,
          sources: column.sources.map((source) => {
            const template = riskTemplates.find((t) => t.id === source.templateId)
            const field = template?.fields.find((f) => f.id === source.fieldId)

            return {
              fieldId: { label: field?.label, value: source.fieldId },
              templateId: { label: template?.name, value: source.templateId }
            }
          })
        }))
      }
    } catch (e) {
      handleError(e, 'AddRiskViewForm.getInitialValue e')
    }
  }

  const createRiskView = async (data: RiskViewFormData) => {
    try {
      const input = getRiskViewInput(data)

      const {
        data: {
          riskViewCreate: { data: riskView }
        }
      } = await riskViewCreate({
        variables: {
          input
        }
      })

      dispatch(addRiskView(riskView))
      selectRiskView(riskView)
      hideModal()
    } catch (e) {
      handleError(e, 'AddRiskViewDrawer. createRiskView error')
    }
  }

  const updateRiskView = async (data: RiskViewFormData) => {
    try {
      const input = getRiskViewInput(data)

      await riskViewUpdate({
        variables: {
          id: riskView.id,
          input: omit(input, 'riskTemplateId')
        }
      })

      const updatedRiskView: IRiskView = {
        ...riskView,
        name: input.name,
        settings: { fields: input.settings.fields },
        customColumns: input.customColumns,
        availableToUserOrPositionIds: input.availableToUserOrPositionIds
      }

      dispatch(updateRiskViewAction({ id: riskView.id, data: updatedRiskView }))
      selectRiskView(updatedRiskView)
      hideModal()
    } catch (e) {
      handleError(e, 'AddRiskViewDrawer. updateRiskView error')
    }
  }

  const onFormSubmit = (data: RiskViewFormData) => (riskView?.id ? updateRiskView(data) : createRiskView(data))

  const initialValues = useInitialValues(getInitialValue, riskView)

  return (
    <DrawerModal width='500px' isOpen={isOpen} hideModal={hideModal} data-testid='risk-view-drawer'>
      <Header>
        {`${riskView?.id ? 'Update' : 'Create'} View`}
        <CloseIcon onClick={hideModal} />
      </Header>
      <AddRiskViewForm isViewUpdate={!!riskView?.id} onFormSubmit={onFormSubmit} initialValues={initialValues} />
    </DrawerModal>
  )
}

export default AddRiskViewDrawer
